/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: PostViewRatings
 * File     : PostViewRatings.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the PostViewRatings view and coordinating data fetching.
 *   Exports: PostViewRatings
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/material
 *   - date-fns
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/postviewratings" element={<PostViewRatings />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Chip, Button, Stack, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRatingOpportunities } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const PostViewRatings = () => {
  const { user } = useAuth();
  const [ops, setOps] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    getRatingOpportunities().then((r) => setOps(r.opportunities || [])).catch(() => setOps([]));
  }, [user]);

  if (!user) return <Typography>Sign in to view rating opportunities</Typography>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Rating Opportunities</Typography>
      <Paper sx={{ p: { xs: 1, sm: 0 } }}>
        <List>
          {ops.length === 0 && (
            <ListItem>
              <ListItemText primary="No rating opportunities right now." secondary="You will see items here after you’ve watched the winning movie and the runtime has elapsed." />
            </ListItem>
          )}
          {ops.filter(o => o && o.pollId).map((o) => {
            const at = o.eligibleAt ? new Date(o.eligibleAt).getTime() : null;
            const now = Date.now();
            const remainingMs = at ? at - now : 0;
            const open = at ? remainingMs <= 0 : true;
            const hh = Math.floor(Math.max(0, remainingMs) / 3600000);
            const mm = Math.floor((Math.max(0, remainingMs) % 3600000) / 60000);
            const ss = Math.floor((Math.max(0, remainingMs) % 60000) / 1000);
            return (
              <ListItem key={o.pollId} divider secondaryAction={<Button variant="contained" onClick={() => navigate(`/results/${o.pollId}`)} disabled={!open}>Rate</Button>}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1">{o.movieTitle}</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip size="small" label={o.pollTitle} />
                    {open ? (
                      <Chip size="small" color="success" label={`Eligible ${formatDistanceToNow(new Date(o.eligibleAt))} ago`} />
                    ) : (
                      <Chip size="small" label={`Opens in ${hh}h ${mm}m ${ss}s`} />
                    )}
                  </Stack>
                </Stack>
              </ListItem>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

export default PostViewRatings;


